import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSelector } from "react-redux";

// socket.io-client v2 — matches the backend server (socket.io v2, EIO=3)
import io from "socket.io-client";
import { DEBUG, debugLog } from "../Utilis/debug";

type SocketEvent =
  | "connect"
  | "disconnect"
  | "connect_error"
  | "new-message"
  | "update-message"
  | "remove-contact"
  | "add-contact"
  | "post"
  | "comment"
  | "newOrder"
  | "changeOrderPay"
  | "cancelOrder"
  | "returnOrder"
  | "newActivity"
  | "activityReply"
  | "activityStatusChanged";

type SocketResponse = {
  error?: string;
  result?: string;
};

type Context = {
  connected: boolean;
  emit: <T>(
    event: SocketEvent,
    data: T,
    callback?: (res: SocketResponse) => void
  ) => void;
  subscribe: <T extends { [key: string]: any; data?: any[] }>(
    event: SocketEvent,
    callback: (data: T) => void
  ) => void;
  unsubscribe: <T>(event: SocketEvent, callback: (data: T) => void) => void;
  socket: any | null;
};

type Props = {
  children: ReactNode;
};

const SocketContext = createContext<Context | null>(null);

export const SocketContextProvider = ({ children }: Props) => {
  const { userInfo, company } = useSelector((state: any) => state.setReducer);
  const [socket, setSocket] = useState<any | null>(null);
  const [connected, setConnected] = useState(false);
  const [pingActive, setPingActive] = useState(true);

  const resolveSocketUrl = useCallback(() => {
    const fromStorage = localStorage.getItem("socketUrl");
    const fromEnv = import.meta.env.VITE_SOCKET_URL;
    const fromCompany =
      company?.socket_url ||
      company?.socketUrl ||
      company?.company_information?.socket_url ||
      company?.company_information?.socketUrl;

    if (fromStorage) return fromStorage;
    if (fromEnv) return fromEnv;
    if (fromCompany) return fromCompany;
    return "";
  }, [company]);

  const emit = useCallback(
    (event: SocketEvent, data: any, cb?: (res: SocketResponse) => void) => {
      socket?.emit(event, data, cb);
    },
    []
  );

  const subscribe = useCallback(
    (event: SocketEvent, listener: (data: any) => void) => {
      socket?.on(event, listener);
    },
    []
  );

  const unsubscribe = useCallback(
    (event: SocketEvent, listener: (data: any) => void) => {
      socket?.off(event, listener);
    },
    []
  );

  const connect = useCallback(() => {
    const token = localStorage.getItem("token");
    const socketUrl = resolveSocketUrl();

    // Normalize common misconfigurations:
    // - API URL like https://host/api/v1/  -> https://host
    // - trailing slashes
    const normalizedSocketUrl = String(socketUrl)
      .replace(/\/api\/v\d+\/?$/i, "")
      .replace(/\/api\/?$/i, "")
      .replace(/\/+$/, "");

    if (!token || !socketUrl || !userInfo || !userInfo.id) {
      console.warn("[Socket] Skipping connect:", {
        hasToken: !!token,
        hasSocketUrl: !!socketUrl,
        hasUserInfo: !!userInfo,
        hasUserId: !!userInfo?.id,
      });
      return;
    }

    // Match the Blade dashboard's connection config exactly
    const sock = io.connect(normalizedSocketUrl, {
      query: `user_id=${userInfo.id}&page=${window.location.pathname}`,
      transports: ["polling"],
      path: "/socket.io",
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      timeout: 10000,
    });

    sock.on("connect", () => {
      console.log(
        "[Socket] Connected. id:",
        sock.id,
        "transport:",
        (sock as any).io?.engine?.transport?.name
      );
      setConnected(true);
    });

    sock.on("connect_error", (err: any) => {
      console.error("[Socket] connect_error:", err?.message ?? err);
    });

    sock.on("pong", () => setPingActive(true));

    sock.on("disconnect", (reason: string) => {
      console.warn("[Socket] Disconnected. reason:", reason);
      setConnected(false);

      if (
        reason === "io server disconnect" ||
        reason === "io client disconnect"
      ) {
        sock.connect();
      }
    });

    // v2 doesn't have onAny, but we can monkey-patch onevent for debug.
    // Dev only: it dumps every payload and reassigns a library internal.
    if (DEBUG) {
      const originalOnevent = (sock as any).onevent;
      (sock as any).onevent = function (packet: any) {
        const args = packet.data || [];
        debugLog("[Socket] event:", args[0], "payload:", args.slice(1));
        originalOnevent.call(sock, packet);
      };
    }

    setSocket(sock);

    return () => {
      sock.disconnect();
    };
  }, [resolveSocketUrl, userInfo]);

  useEffect(() => {
    if (!userInfo) return;
    const cleanupSocket = connect();
    return () => {
      cleanupSocket?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  useEffect(() => {
    if (!socket) return;
    const timer = setTimeout(() => {
      if (!pingActive) setConnected(false);
      setPingActive(false);
      socket.emit("ping");
    }, 90000);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, socket, pingActive]);

  return (
    <SocketContext.Provider
      value={{
        connected,
        subscribe,
        unsubscribe,
        emit,
        socket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => {
  const context = useContext(SocketContext);

  if (!context)
    throw Error("Error using SocketContext outside SocketContextProvider");
  return context;
};
